/**
 * @architecture-header 1
 * @layer module
 * @domain live
 * @responsibility Render the hand-off screen with its three resizable columns.
 * @state-owner dataset store; page-local draft message
 * @allowed-imports React; same-module mutations; dataset and workspace stores; shared components, selectors and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects seat status updates, navigation and toasts
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useState } from 'react';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { BackButton } from '../../../shared/components/PageShell';
import { PanelResizer } from '../../../shared/components/Resizer';
import type { RunRecord } from '../../../shared/lib/model';
import {
  currentTimeLabel,
  findSeat,
  runName,
  seatPresentation,
  seatStatusLabel,
} from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useResolveIntervention, useSendInterventionMessage } from '../api/live.mutations';
import { buildHandoff, buildHandoffNote, CERTAINTY_LABEL } from '../model/handoff';
import {
  canHandBack,
  type InterventionMode,
  MODE_HINT,
  MODE_LABEL,
  sessionSummary,
  startSession,
} from '../model/intervention';
import { selectOpenSession, useInterventions } from '../store/intervention';
import { AgentPolicyDialog } from './AgentPolicyDialog';

const QUICK_LINES = [
  '把 -3 移到右邊，符號會變成什麼？',
  '先別算，等號兩邊各加 3 看看',
  '你這一步的 -3 去哪了？',
];

export function InterventionPage({ run, student }: { run: RunRecord; student: string }) {
  const t = useTranslator();
  const { openModal } = useOverlay();
  const activities = useDatasetStore((state) => state.activities);
  const classes = useDatasetStore((state) => state.classes);
  const seats = useDatasetStore((state) => state.seats[run.classIndex] ?? []);
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  const closeItem = useWorkspaceUiStore((state) => state.closeItem);
  const navigate = useWorkspaceUiStore((state) => state.navigate);
  const notify = useWorkspaceUiStore((state) => state.notify);
  const [message, setMessage] = useState('');
  const [openField, setOpenField] = useState<string | null>(null);
  /** Kept apart from the brief: what the teacher did is not what the assistant observed. */
  const [notes, setNotes] = useState<readonly string[]>([]);
  const sessions = useInterventions((state) => state.sessions);
  const startSessionIn = useInterventions((state) => state.start);
  const patchSession = useInterventions((state) => state.patch);
  const session = selectOpenSession(sessions, student);

  const beginSession = (mode: InterventionMode) => {
    if (session) {
      patchSession(session.id, { mode });
      return;
    }
    startSessionIn(startSession(`iv${sessions.length + 1}`, student, mode, currentTimeLabel()));
  };

  /** Closing the card writes the hand-back note; the assistant resumes from there, not from zero. */
  const handBack = (id: string) => {
    patchSession(id, { endedAt: currentTimeLabel() });
    notify(t('已交回助教'));
  };
  const sendMessage = useSendInterventionMessage();
  const resolveIntervention = useResolveIntervention();

  const seat = findSeat(seats, student);
  const brief = buildHandoff(
    seat ?? {
      name: student,
      progress: 0,
      status: 'ok',
      lastActiveMinutes: 0,
      aiAttempts: 0,
      confidence: 'low',
    },
  );
  const resolved = seat?.status === 'ok' || seat?.status === 'online';

  /** and reports the resolution outside demo mode. */
  const resolve = (toast?: string, { close = true }: { close?: boolean } = {}) => {
    resolveIntervention.mutate({ classId: classes[run.classIndex].id, studentId: student });
    useDatasetStore.getState().setSeatStatus(run.classIndex, student, 'ok');
    if (close) closeItem();
    notify(toast ?? t('已標記解決'));
  };

  /**
   * and delivers the line through the DM transport. Sending
   * keeps the teacher on the screen: the roster beside it already turns the handled student from
   * red back to neutral, so who still needs help stays readable without navigating away.
   */
  const send = () => {
    const line = message.trim();
    if (!line) {
      notify(t('先寫一句話'));
      return;
    }
    sendMessage.mutate({ classId: classes[run.classIndex].id, message: line, studentId: student });
    setNotes((current) => [...current, buildHandoffNote(student, line, currentTimeLabel())]);
    setMessage('');
    resolve(t('已送出，AI 接手續陪'), { close: false });
  };

  return (
    <div className="ivgrid">
      <div className="ivl">
        <PanelResizer max={380} min={160} panel="ivl" />
        <div className="sec5" style={{ margin: '0 0 8px 4px' }}>
          {t('這堂課')}
        </div>
        {seats.map((item) => {
          const waiting = seatPresentation(item.status).needsTeacher;
          return (
            <button
              className={`ivs ${item.name === student ? 'on' : ''} ${waiting ? 'hot' : ''}`}
              key={item.name}
              type="button"
              onClick={() => openItem({ k: 'iv', n: item.name })}
            >
              <span className={`av sm ${waiting ? 'e' : ''}`}>{item.name[0]}</span>
              <span className="t">
                <b>{item.name}</b>
                <span>{seatStatusLabel(item, t)}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="ivm">
        <div className="hd" style={{ marginBottom: 20 }}>
          <BackButton onSelect={closeItem} title={`${t('返回')} ${t('面板')}`} />
          <div>
            <h1>{student}</h1>
            <p className="sub">{`${runName(activities, run)} · ${t('第 3-2 題')}`}</p>
          </div>
          <div className="sp" />
          <button
            className="bt"
            type="button"
            onClick={() => openModal(<AgentPolicyDialog runId={run.id} student={student} />)}
          >
            {t('調整 AI 助教')}
          </button>
          <button className="bt" disabled={resolved} type="button" onClick={() => resolve()}>
            {t('標記已解決')}
          </button>
        </div>
        {/* Seven fixed fields instead of a transcript: the teacher reads the case, not the chat. */}
        <div className="hbmeta">
          <span>{t(brief.window)}</span>
          <em>{`v${brief.version}`}</em>
        </div>
        {brief.fields.map((field) => (
          <div className="hbf" key={field.id}>
            <div className="hbh">
              <b>{t(field.label)}</b>
              <span className={`hbc ${field.certainty}`}>
                {t(CERTAINTY_LABEL[field.certainty])}
              </span>
              <div className="sp" />
              {field.evidence.length ? (
                <button
                  className="lk"
                  type="button"
                  onClick={() => setOpenField(openField === field.id ? null : field.id)}
                >
                  {openField === field.id
                    ? t('收合依據')
                    : `${t('看依據')} (${field.evidence.length})`}
                </button>
              ) : null}
            </div>
            <p className="hbv">{t(field.value)}</p>
            {openField === field.id ? (
              <ul className="hbe">
                {field.evidence.map((item) => (
                  <li className={item.from} key={`${item.at}-${item.text}`}>
                    {item.at ? <em>{item.at}</em> : null}
                    {t(item.text)}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
        {notes.length ? (
          <div className="hbnote">
            <b>{t('介入紀錄')}</b>
            {notes.map((note) => (
              <p key={note}>{note}</p>
            ))}
          </div>
        ) : null}
        {/* Four ways in, not one. Taking the channel is a choice, not the only door. */}
        <div className="sec5">{t('你要怎麼介入')}</div>
        <div className="fbar" style={{ margin: '0 0 12px' }}>
          {(Object.keys(MODE_LABEL) as InterventionMode[]).map((key) => (
            <button
              className={`ch ${session?.mode === key ? 'on' : ''}`}
              key={key}
              title={t(MODE_HINT[key])}
              type="button"
              onClick={() => beginSession(key)}
            >
              {t(MODE_LABEL[key])}
            </button>
          ))}
        </div>
        {session ? (
          <div className="ivcard">
            <b>{sessionSummary(session)}</b>
            <span>{t(MODE_HINT[session.mode])}</span>
            <label className="ivrs">
              <input
                checked={session.askRestate}
                onChange={(event) => patchSession(session.id, { askRestate: event.target.checked })}
                type="checkbox"
              />
              {t('交回助教前，請學生用自己的話說一次')}
            </label>
            <textarea
              className="vcmt"
              onChange={(event) => patchSession(session.id, { handBackNote: event.target.value })}
              placeholder={t('交接給助教的一句話，例如：他已經懂變號，接下來練兩題就好')}
              rows={2}
              value={session.handBackNote ?? ''}
            />
            {session.askRestate ? (
              <p className="ivwait">
                {session.restatement
                  ? `${t('學生說')}：${session.restatement}`
                  : t('等學生用自己的話重述後才能交回助教')}
              </p>
            ) : null}
            <div className="rowbt">
              <button
                className="bt sm pri"
                disabled={!canHandBack(session)}
                type="button"
                onClick={() => handBack(session.id)}
              >
                {t('交回助教')}
              </button>
            </div>
          </div>
        ) : null}
        <div className="sec5">{t('傳一段話給他')}</div>
        <div className="ansrow" style={{ marginTop: 0 }}>
          <input
            aria-label={t('介入訊息')}
            id="ivmsg"
            onChange={(event) => setMessage(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') send();
            }}
            placeholder={t('用一句話點出他卡住的那一步')}
            style={{ flex: 1 }}
            value={message}
          />
          <button className="bt pri" type="button" onClick={send}>
            {t('送出')}
          </button>
        </div>
        <div className="fbar" style={{ margin: '14px 0 0' }}>
          {QUICK_LINES.map((line) => (
            <button className="ch" key={line} type="button" onClick={() => setMessage(t(line))}>
              {t(line)}
            </button>
          ))}
        </div>
      </div>

      <div className="ivr">
        <PanelResizer max={480} min={240} panel="ivr" side="l" />
        {/* The rail keeps only what the brief does not cover: this student over the term. */}
        <div className="sec5">{t('他這學期')}</div>
        <div className="cmini">
          <span>{t('移項變號')}</span>
          <span className="cv3">
            <i style={{ width: '47%', background: 'var(--error)' }} />
          </span>
          <em>47%</em>
        </div>
        <div className="cmini">
          <span>{t('列式')}</span>
          <span className="cv3">
            <i style={{ width: '86%', background: 'var(--success)' }} />
          </span>
          <em>86%</em>
        </div>
        <button
          className="bt"
          style={{ width: '100%', marginTop: 14 }}
          type="button"
          onClick={() => navigate('rep', student)}
        >
          {t('檢視完整學習報告')}
        </button>
      </div>
    </div>
  );
}
